import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Edit2, Plus, ArrowLeft } from 'lucide-react';
import { 
  DetailChecklist, 
  DetailOverviewTab, 
  DetailResourcesTab,
  DetailSidebar, 
  DetailSummaryHeader, 
  DetailSummaryInfo, 
  ProgressUpdateModal,
  ResourceAssignmentModal 
} from './components';
import { useWorkOrderDetail } from './hooks/useWorkOrderDetail';
import { workOrderDetailService } from './services/workOrderDetail.service';
import { toast } from 'sonner';

const tabs = [
  { id: 'overview', label: 'Tổng quan' },
  { id: 'checklist', label: 'Công việc con' },
  { id: 'resources', label: 'Nguồn lực' },
  { id: 'progress', label: 'Tiến độ' },
  { id: 'documents', label: 'Tài liệu' },
  { id: 'cost', label: 'Chi phí' },
  { id: 'activity', label: 'Nhật ký' },
  { id: 'history', label: 'Lịch sử' },
];

export default function WorkOrdersDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { workOrder, isLoading, refetch } = useWorkOrderDetail(id || '');
  const [activeTab, setActiveTab] = useState('overview');
  const [isProgressModalOpen, setIsProgressModalOpen] = useState(false);
  const [isResourceModalOpen, setIsResourceModalOpen] = useState(false);
  const [resourceType, setResourceType] = useState<'material' | 'equipment'>('material');

  const handleRemoveResource = async (id: string, type: 'material' | 'equipment') => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa nguồn lực này?')) return;
    try {
      await workOrderDetailService.removeResource(id, type);
      toast.success('Đã xóa nguồn lực thành công');
      refetch();
    } catch (error: any) {
      toast.error('Lỗi khi xóa: ' + error.message);
    }
  };

  const backPath = '/work-orders';
  const editPath = `/work-orders/edit/${id}`;

  if (isLoading) {
    return (
      <main className="min-h-screen space-y-4 bg-slate-50 p-3">
        <div className="h-28 animate-pulse rounded-md bg-slate-100" />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
          <div className="h-96 animate-pulse rounded-md bg-slate-100 lg:col-span-8" />
          <div className="h-96 animate-pulse rounded-md bg-slate-100 lg:col-span-4" />
        </div>
      </main>
    );
  }

  if (!workOrder) {
    return (
      <main className="min-h-screen bg-slate-50 p-3">
        <div className="rounded-md border border-slate-100 bg-white p-3 text-center">
          <p className="sm:text-sm text-xs text-slate-600">Không tìm thấy dữ liệu lệnh thi công.</p>
          <button type="button" onClick={() => navigate(backPath)} className="mt-3 rounded-md bg-emerald-700 px-3 py-2 sm:text-sm text-xs text-white">
            Quay lại danh sách
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen space-y-4 bg-slate-50 p-3 text-slate-900">
      {/* Nút Quay lại đứng riêng bên ngoài */}
      <div className="flex items-center">
        <button type="button" onClick={() => navigate(backPath)} className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5 sm:text-sm text-xs text-slate-600 hover:bg-slate-50 transition-colors">
          <ArrowLeft className="h-4 w-4" />
          <span className="font-medium">Quay lại</span>
        </button>
      </div>

      {/* Khối thống nhất (Tiêu đề, Thao tác, Lưới thông tin) */}
      <div className="rounded-md border border-slate-100 bg-white p-4 space-y-4">
        <DetailSummaryHeader data={workOrder} />

        {/* Cụm Nút Thao tác */}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button type="button" onClick={() => navigate(editPath)} className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-3 sm:text-sm text-xs text-slate-700 hover:bg-slate-50 transition-colors">
            <Edit2 className="h-4 w-4" />
            Chỉnh sửa
          </button>
          <button type="button" onClick={() => setIsProgressModalOpen(true)} className="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-emerald-700 px-3 sm:text-sm text-xs text-white hover:bg-emerald-800 transition-all">
            <Plus className="h-4 w-4" />
            Cập nhật tiến độ
          </button>
        </div>

        <div className="h-px w-full bg-slate-100" />

        <DetailSummaryInfo data={workOrder} />
      </div>

      <section className="space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-slate-100 pb-2 lg:pb-0">
          <div className="flex gap-1 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`whitespace-nowrap border-b-2 px-3 py-2 sm:text-sm text-xs transition-colors ${
                  activeTab === tab.id
                    ? 'border-emerald-700 text-emerald-600'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
          <div className="lg:col-span-8">
            {activeTab === 'overview' ? (
              <DetailOverviewTab data={workOrder} />
            ) : activeTab === 'checklist' ? (
              <DetailChecklist checklists={workOrder.checklists} />
            ) : activeTab === 'resources' ? (
              <DetailResourcesTab 
                data={workOrder} 
                onAssign={(type) => {
                  setResourceType(type);
                  setIsResourceModalOpen(true);
                }}
                onRemove={handleRemoveResource}
              />
            ) : (
              <div className="rounded-md border border-slate-100 bg-white p-3 sm:text-sm text-xs text-slate-500">
                Dữ liệu tab {tabs.find((tab) => tab.id === activeTab)?.label} sẽ được hiển thị tại đây khi nghiệp vụ được mở rộng.
              </div>
            )}
          </div>
          <div className="lg:col-span-4">
            <DetailSidebar data={workOrder} />
          </div>
        </div>
      </section>

      <ProgressUpdateModal
        isOpen={isProgressModalOpen}
        onClose={() => setIsProgressModalOpen(false)}
        data={workOrder}
        onSuccess={() => refetch()}
      />

      <ResourceAssignmentModal
        isOpen={isResourceModalOpen}
        onClose={() => setIsResourceModalOpen(false)}
        data={workOrder}
        type={resourceType}
        onSuccess={() => refetch()}
      />
    </main>
  );
}
